// One-line previews (the hub's "Waiting on you" cards) show the STORED message
// string, which per D6 is markdown. Rendering it through ChatMarkdown there is
// wrong for a single truncated line (block elements, lists), and showing it raw
// put literal `**Samuel Nathan**` in the preview the moment mentions started
// serializing to bold. Strip the marks to plain text instead.
//
// This intentionally only handles what serialize.ts emits: **bold**, *italic*,
// ~~strike~~, ++underline++, [label](dest), list markers, and backslash escapes.
//
// ESCAPABLE must stay in step with BOTH escapers in serialize.ts —
// escapeLiterals (inline marks) and escapeBlockStart (line-start block markers
// `#`, `>`, `-`, `+`, and the `.` / `)` of an ordered item). Miss one and the
// raw backslash leaks into every preview surface: OS notification bodies, the
// composer's reply banner, the hub's unread cards, message quote blocks.
//
// The index is TWO digits, and the decoder below reads two. A single digit
// silently corrupted everything past the tenth entry — `\u000010` decoded as
// entry 1 followed by a stray "0" — and adding the block markers took this set
// from eight entries to thirteen.
const ESCAPABLE = "\\*_~[]`+#>-.)";
const ESCAPED_LITERAL = /\\([\\*_~[\]`+#>\-.)])/g;
const INDEX_WIDTH = 2;

export function previewText(markdown: string): string {
  return (
    markdown
      // Encode backslash-escaped literals to a NUL+index pair first, so the
      // mark-stripping below cannot see them — `\*stars\*` must survive as
      // `*stars*`. The special character is REPLACED rather than merely
      // prefixed: leaving it in the string lets the mark-stripper below eat it
      // anyway, which is the whole point of parking it. NUL cannot occur in a
      // serializer-produced message string.
      .replace(
        ESCAPED_LITERAL,
        (_match, ch: string) =>
          `\u0000${String(ESCAPABLE.indexOf(ch)).padStart(INDEX_WIDTH, "0")}`
      )
      // Links: angle-bracket destinations first (they may contain a `)`).
      .replace(/\[([^\]]*)\]\(<[^>]*>\)/g, "$1")
      .replace(/\[([^\]]*)\]\([^)\s]*\)/g, "$1")
      .replace(/(\*\*|~~|\+\+|\*)/g, "")
      // List markers at the start of a line.
      .replace(/^\s*(?:-|\d+\.)\s+/gm, "")
      .replace(/\n+/g, " ")
      .replace(
        // eslint-disable-next-line no-control-regex -- NUL sentinel above
        /\u0000(\d{2})/g,
        (_match, index: string) => ESCAPABLE[Number(index)]
      )
      .trim()
  );
}
